import { inject, signal } from '@angular/core';
import { PDF, PDFColor } from '@grid-ui/pdf-generator';
import { asyncForEach, delay } from '@maplecroft-typescript/utils';
import { BodyContentItem, CountryRiskIndexNarrative, CountryRiskIndexNarrativeSectionMethodology } from '../../../shared-models';
import { PDFService } from '../../../shared-services';

export abstract class IndexInfoPdf {
  private readonly pdfService = inject(PDFService);

  private readonly pdfRemainingBeforeHeightNewPage = 50;

  public readonly dowloadingPdf = signal<boolean>(false);

  public async downloadPdf(narrative: CountryRiskIndexNarrative): Promise<void> {
    this.dowloadingPdf.set(true);

    const {
      name,
      content: {
        sections: {
          definition: { definition_of_index, definition_of_issue, characteristics_of_index },
          methodology: {
            objective = [],
            construction = [],
            limitations = [],
            research = [],
          } = {} as CountryRiskIndexNarrativeSectionMethodology,
          sources = [],
        },
      },
    } = narrative;

    // Wait fo the processing indicator to render before generating PDF
    await delay(100);

    const pdf = this.pdfService.newPDF({});

    // Title
    pdf.addText(name, {
      size: 20,
      weight: 'bold',
      spacingTop: 0,
      spacingBottom: 5,
      color: PDFColor.Secondary,
    });

    await this.addPdfSection(pdf, 'Definition', [
      {
        title: 'Definition of the Index',
        items: definition_of_index,
      },
      {
        title: 'Definition of the Issue',
        items: definition_of_issue,
      },
      {
        title: 'Characteristics of the Index',
        items: characteristics_of_index,
      },
    ]);

    await this.addPdfSection(pdf, 'Methodology', [
      {
        title: 'Objective',
        items: objective,
      },
      {
        title: 'Construction',
        items: construction,
      },
      {
        title: 'Limitations',
        items: limitations,
      },
      {
        title: 'Research',
        items: research,
      },
    ]);

    if (sources.length) {
      if (pdf.startY >= pdf.pageContentHeight - this.pdfRemainingBeforeHeightNewPage) {
        pdf.addPage();
      }

      pdf.addText('Sources', {
        size: 17,
        weight: 'bold',
        color: PDFColor.Primary,
        spacingTop: 0,
      });

      await this.pdfService.addBodyItems(pdf, sources);
    }

    pdf.addDisclaimerPage();

    pdf.save(`Verisk Maplecroft - ${name}.pdf`);

    this.dowloadingPdf.set(false);
  }

  private async addPdfSection(pdf: PDF, title: string, subsections: { title: string; items: BodyContentItem[] }[]): Promise<void> {
    if (subsections.every(({ items }) => items.length === 0)) {
      return;
    }

    if (pdf.startY >= pdf.pageContentHeight - this.pdfRemainingBeforeHeightNewPage) {
      pdf.addPage();
    }

    pdf.addText(title, {
      size: 17,
      weight: 'bold',
      color: PDFColor.Primary,
      spacingTop: 0,
    });

    // eslint-disable-next-line @typescript-eslint/no-shadow
    await asyncForEach(subsections, async ({ title, items }) => {
      if (items.length === 0) {
        return;
      }

      if (pdf.startY >= pdf.pageContentHeight - this.pdfRemainingBeforeHeightNewPage) {
        pdf.addPage();
      }

      pdf.addText(title, {
        weight: 'bold',
        size: 15,
      });

      await this.pdfService.addBodyItems(pdf, items);
    });

    pdf.startY += 10;
  }
}
